// src/pages/AdminGpnBudgetPurchases.jsx
//
// Admin-facing page: review pending GEFEN budget-purchase requests
// (card / PayPal / bank transfer) and — for a bank transfer — confirm
// it only after actually seeing the money in the bank statement,
// entering the real transfer date, payer name and bank reference
// number at that moment. Confirming credits the school's program
// budget immediately (SchoolBudgetController::confirmBankTransfer).
import React, { useEffect, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { useGpnBudgetPurchases } from '../contexts/GpnBudgetPurchasesContext';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';

const formatMoney = (v) => `₪${Number(v || 0).toLocaleString('he-IL', { maximumFractionDigits: 0 })}`;

const STATUS_LABELS = {
  PENDING: 'ממתין',
  PAID: 'שולם',
  CANCELLED: 'בוטל',
  FAILED: 'נכשל',
};
const STATUS_STYLES = {
  PENDING: 'bg-amber-50 text-amber-700',
  PAID: 'bg-emerald-50 text-emerald-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
  FAILED: 'bg-red-50 text-red-600',
};
const METHOD_LABELS = {
  CARD: 'כרטיס אשראי',
  PAYPAL: 'PayPal',
  BANK_TRANSFER: 'העברה בנקאית',
};

const ConfirmTransferModal = ({ purchase, onClose, onConfirmed }) => {
  const { showAlert } = useUser();
  const { confirmBankTransfer, viewReceipt } = useGpnBudgetPurchases();

  const [transferDate, setTransferDate] = useState(new Date().toISOString().slice(0, 10));
  const [payerName, setPayerName] = useState('');
  const [bankReference, setBankReference] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!transferDate || !payerName.trim() || !bankReference.trim()) {
      showAlert?.('יש למלא תאריך העברה, שם משלם ואסמכתא בנק', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      await confirmBankTransfer(purchase.id, {
        transfer_date: transferDate,
        payer_name: payerName.trim(),
        bank_reference: bankReference.trim(),
        admin_note: adminNote.trim() || undefined,
      });
      showAlert?.('ההעברה אושרה והתקציב עודכן', 'success');
      onConfirmed?.();
      onClose();
    } catch (err) {
      showAlert?.(err.message || 'שגיאה באישור ההעברה', 'warning');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-3" dir="rtl">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
        <h2 className="text-base font-bold text-gray-800 mb-1">אישור העברה בנקאית</h2>
        <p className="text-xs text-gray-500 mb-4">
          {purchase.schoolName} — {purchase.programName} · {formatMoney(purchase.amount)}
          <br />
          קוד אסמכתא שנמסר לבית הספר: <b>{purchase.referenceCode}</b>
        </p>

        {purchase.hasReceipt ? (
          <button
            type="button"
            onClick={() => viewReceipt(purchase.id).catch((err) => showAlert?.(err.message || 'שגיאה בפתיחת המסמך', 'warning'))}
            className="w-full mb-3 py-2 rounded-lg border border-indigo-200 text-indigo-700 text-sm font-medium hover:bg-indigo-50"
          >
            📄 צפייה באישור ההעברה שנשלח על ידי בית הספר
          </button>
        ) : (
          <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">
            בית הספר עדיין לא צירף אישור העברה בקובץ PDF. מומלץ להמתין לקובץ, או לוודא מול הבנק לפני האישור הסופי.
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">תאריך ההעברה בפועל</label>
            <input
              type="date"
              value={transferDate}
              onChange={(e) => setTransferDate(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">שם המשלם (כפי שמופיע בדף הבנק)</label>
            <input
              type="text"
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
              placeholder="לדוגמה: בית ספר תיכון X"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">מספר אסמכתא בבנק</label>
            <input
              type="text"
              value={bankReference}
              onChange={(e) => setBankReference(e.target.value)}
              placeholder="מספר האסמכתא כפי שמופיע בדף הבנק שלנו"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">הערה (אופציונלי)</label>
            <textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-600"
          >
            ביטול
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="flex-1 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold disabled:opacity-50"
          >
            {submitting ? 'מאשר…' : 'אשר וזכה תקציב'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminGpnBudgetPurchases = () => {
  const { showAlert } = useUser();
  const { items, loading, fetchPurchases } = useGpnBudgetPurchases();
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [confirmTarget, setConfirmTarget] = useState(null);

  const load = () => {
    fetchPurchases({ status: statusFilter || undefined }).catch((err) => {
      showAlert?.(err.message || 'שגיאה בטעינת בקשות התשלום', 'warning');
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  return (
    <div className="min-h-screen bg-gray-100" dir="rtl">
      <div className="mx-auto max-w-5xl px-3 sm:px-6 py-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-lg font-bold text-gray-800">רכישות תקציב גפ״ן</h1>
            <p className="text-xs text-gray-500 mt-1">אישור העברות בנקאיות ומעקב אחר תשלומי כרטיס/PayPal.</p>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white"
          >
            <option value="PENDING">ממתינים</option>
            <option value="PAID">שולמו</option>
            <option value="FAILED">נכשלו</option>
            <option value="CANCELLED">בוטלו</option>
            <option value="">הכל</option>
          </select>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-right text-xs text-gray-500 border-b border-gray-100">
                <th className="px-4 py-3 font-medium">בית ספר</th>
                <th className="px-4 py-3 font-medium">תוכנית</th>
                <th className="px-4 py-3 font-medium">סכום</th>
                <th className="px-4 py-3 font-medium">אמצעי</th>
                <th className="px-4 py-3 font-medium">סטטוס</th>
                <th className="px-4 py-3 font-medium">אסמכתא</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-gray-50 last:border-0">
                  <td className="px-4 py-3">{p.schoolName}</td>
                  <td className="px-4 py-3 text-gray-600">{p.programName}</td>
                  <td className="px-4 py-3 font-semibold">{formatMoney(p.amount)}</td>
                  <td className="px-4 py-3 text-gray-600">{METHOD_LABELS[p.method] || p.method}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLES[p.status] || ''}`}>
                      {STATUS_LABELS[p.status] || p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {p.referenceCode}
                    {p.bankReference && <div className="text-gray-400">בנק: {p.bankReference}</div>}
                    {p.method === 'BANK_TRANSFER' && (
                      <div className={p.hasReceipt ? 'text-emerald-600' : 'text-gray-400'}>
                        {p.hasReceipt ? '📄 יש אישור' : 'אין אישור עדיין'}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {p.method === 'BANK_TRANSFER' && p.status === 'PENDING' && (
                      <button
                        onClick={() => setConfirmTarget(p)}
                        className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                      >
                        אשר העברה
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">
                    אין בקשות תשלום להצגה.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {confirmTarget && (
        <ConfirmTransferModal
          purchase={confirmTarget}
          onClose={() => setConfirmTarget(null)}
          onConfirmed={load}
        />
      )}

      {loading && <SpinnerOverlay />}
    </div>
  );
};

export default AdminGpnBudgetPurchases;